import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { enderecoDeArquivo } from '@/shared/api/client';
import {
  brokerQuotes,
  operations,
  policies,
  quoteFinancial,
  quoteTimeline,
  type TimelineEntry,
} from '@/shared/api/resources';
import {
  formatCivilDate,
  formatDateTime,
  formatDocument,
  formatMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton, SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { Stepper } from '@/shared/ui/Stepper';
import { Timeline } from '@/shared/ui/Timeline';
import { useToast } from '@/shared/ui/useToast';

import {
  chaveDaApoliceDaCotacao,
  chaveDasParcelasDaCotacao,
  MOTIVO_COTACAO_ENCERRADA,
  MOTIVO_SEM_BOLETO,
  temBoletoNaCotacao,
} from '../../portfolio/boletoAnexado';
import {
  motivoSemDocumento,
  temDocumentoDaApolice,
} from '../../portfolio/documentoDaApolice';
import { itemDaLinhaDoTempo } from '../acompanhamento';
import { CommentsPanel } from '../DemandPanels';
import { nomeDoResumo, salvarArquivoDoFinanceiro } from '../financeiroDaCotacao';

import { BoletosDaCotacao } from './BoletosDaCotacao';
import { BrokerRequestedDocuments } from './BrokerRequestedDocuments';
import { gruposDaMovimentacao } from './cotacaoDaCorretora';
import { IssuanceRequestDialog } from './IssuanceRequestDialog';
import { QuoteWithdrawDialog } from './QuoteWithdrawDialog';

const linkSecundario =
  'inline-flex h-10 items-center rounded-md px-3 text-sm font-medium text-ink-600 transition-colors duration-(--duration-fast) hover:bg-ink-100';

/**
 * A cotacao vista pela corretora (`/propostas/timeline/:id` do OnPoint; NCT-039 a
 * NCT-058).
 *
 * As fases Cotacao, Proposta e Emissao no topo; "Dados da cotacao"; o "Historico da
 * movimentacao" com uma linha por grupo do OnPoint (Cotacao, Cadastro, PEP, Credito,
 * Subscricao, Resseguro, Emissao) e a legenda Aprovado, Recusado, Seguradora e Corretor;
 * e os botoes Baixar resumo, Baixar boleto(s) e Baixar apolice. Sem os paineis de parecer
 * da seguradora, que davam 403 para a corretora. O detalhe interno dos eventos chega so
 * se a empresa escolheu o modo completo: o servidor ja manda o que a corretora le.
 *
 * "Baixar resumo" e o resumo da cotacao do financeiro (P-COT-4, ligado pelo P-NCT-L;
 * NCT-056). "Baixar apolice" acha a apolice da cotacao emitida pela lista de apolices
 * (filtro pela operacao, com o escopo da corretora) e baixa o documento dela (P-MIN-2;
 * NCT-058). "Baixar boleto(s)" abre os boletos que a seguradora anexou as parcelas da
 * cotacao (P-BOL; NCT-057); sem boleto anexado, fica desligado com o motivo. Na cotacao
 * emitida ele abre os boletos da apolice, que copiou e pode ter trocado o da cotacao;
 * na recusada ou cancelada fica desligado, porque nao ha premio a pagar (R5-A08).
 *
 * As acoes da corretora vem do servidor (`quote_actions`): editar e enviar a cotacao
 * antes da analise, cancelar a propria cotacao e seguir para a emissao.
 */
export function BrokerTimeline() {
  const { operationId = '' } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelando, setCancelando] = useState(false);
  const [pedindoEmissao, setPedindoEmissao] = useState(false);
  const [vendoBoletos, setVendoBoletos] = useState(false);

  const operacao = useQuery({
    queryKey: ['operations', operationId],
    queryFn: ({ signal }) => operations.get(operationId, signal),
    enabled: operationId !== '',
  });
  const linha = useQuery({
    queryKey: ['quote-timeline', operationId, 'timeline'],
    queryFn: ({ signal }) => quoteTimeline.timeline(operationId, signal),
    enabled: operationId !== '',
  });
  const info = useQuery({
    queryKey: ['quote-timeline', operationId, 'info'],
    queryFn: ({ signal }) => quoteTimeline.info(operationId, signal),
    enabled: operationId !== '',
  });

  // As parcelas dizem se ha boleto anexado; o arquivo so e pedido no dialogo.
  const parcelas = useQuery({
    queryKey: chaveDasParcelasDaCotacao(operationId),
    queryFn: ({ signal }) => quoteFinancial.installments(operationId, signal),
    enabled: operationId !== '',
    retry: false,
  });
  const comBoleto = temBoletoNaCotacao(parcelas.data);

  const baixarResumo = useMutation({
    mutationFn: () => quoteFinancial.summary(operationId),
    onSuccess: (arquivo) => {
      if (operacao.data) {
        salvarArquivoDoFinanceiro(arquivo, nomeDoResumo(operacao.data, arquivo));
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // A apolice nascida desta cotacao, pela lista de apolices da corretora.
  const emitida = operacao.data?.stage === 'emitida';
  const apolice = useQuery({
    queryKey: chaveDaApoliceDaCotacao(operationId),
    queryFn: ({ signal }) =>
      policies.list({ operation_id: operationId, limit: 1 }, signal),
    enabled: emitida,
    retry: false,
  });
  const baixarApolice = useMutation({
    mutationFn: (policyId: string) => policies.document(policyId),
    onSuccess: (link) =>
      window.open(enderecoDeArquivo(link.url), '_blank', 'noopener,noreferrer'),
    onError: (error: Error) => toast.error(error.message),
  });

  const enviar = useMutation({
    mutationFn: () => brokerQuotes.submit(operationId),
    onSuccess: () => {
      toast.success('Encaminhado para analise manual.');
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operationId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (operacao.isPending || linha.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-64" />
        <Card>
          <CardBody>
            <SkeletonText lines={5} />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (operacao.isError || linha.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Cotacao" />
        <Alert tone="danger" title="Nao foi possivel abrir a cotacao">
          <div className="flex flex-col gap-3">
            <p>{(operacao.error ?? linha.error)?.message}</p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate('/operacoes')}
            >
              Voltar para cotacoes
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  const op = operacao.data;
  const tempo = linha.data;
  const acoes = op.quote_actions;
  const interrompida = tempo.phases.some((fase) => fase.state === 'interrompida');
  const todasConcluidas = tempo.phases.every((fase) => fase.state === 'concluida');
  const grupoDaModalidade = op.modality_info?.group?.label;
  const numero = op.proposal_number ?? String(op.number);
  const apoliceDaCotacao = apolice.data?.items[0];
  // A cotacao emitida abre os boletos da apolice; a encerrada sem emissao nao tem.
  const encerradaSemEmissao = op.stage === 'declinada';
  // A falha na leitura das parcelas nao e "sem boleto": o botao fica ligado, e o
  // dialogo mostra o erro (R5-B25).
  const podeVerBoletos =
    emitida || (!encerradaSemEmissao && (comBoleto || parcelas.isError));
  const motivoSemBoletos = encerradaSemEmissao
    ? MOTIVO_COTACAO_ENCERRADA
    : MOTIVO_SEM_BOLETO;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={`Cotacao ${numero}`}
        description={op.subject}
        meta={
          <>
            {op.proposal_status ? (
              <StatusBadge kind="cotacao" value={op.proposal_status} />
            ) : null}
            <StatusBadge kind="faseCotacao" value={tempo.phase} />
          </>
        }
        actions={
          <>
            <Link to="/operacoes" className={linkSecundario}>
              Lista de cotacoes
            </Link>
            {acoes?.can_edit ? (
              <Button
                variant="secondary"
                onClick={() => void navigate(`/operacoes/${op.id}/editar/tomador`)}
              >
                Editar cotacao
              </Button>
            ) : null}
            {acoes?.can_withdraw ? (
              <Button variant="danger" onClick={() => setCancelando(true)}>
                Cancelar cotacao
              </Button>
            ) : null}
            {acoes?.can_submit ? (
              <Button loading={enviar.isPending} onClick={() => enviar.mutate()}>
                Enviar para analise
              </Button>
            ) : null}
            {acoes?.can_request_issuance ? (
              <Button onClick={() => setPedindoEmissao(true)}>
                Seguir para emissao
              </Button>
            ) : null}
          </>
        }
      />

      <Card>
        <CardBody className="flex flex-col gap-4">
          <Stepper
            label="Fases da cotacao"
            steps={tempo.phases.map((fase) => ({
              key: fase.key,
              label: fase.label,
              state: fase.state === 'concluida' ? 'concluida' : undefined,
            }))}
            currentKey={todasConcluidas ? '' : tempo.phase}
            terminated={interrompida}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              loading={baixarResumo.isPending}
              onClick={() => baixarResumo.mutate()}
            >
              Baixar resumo
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!podeVerBoletos}
              title={podeVerBoletos ? undefined : motivoSemBoletos}
              onClick={() => setVendoBoletos(true)}
            >
              Baixar boleto(s)
            </Button>
            {emitida && apoliceDaCotacao ? (
              <Button
                size="sm"
                variant="secondary"
                loading={baixarApolice.isPending}
                disabled={!temDocumentoDaApolice(apoliceDaCotacao)}
                title={motivoSemDocumento(apoliceDaCotacao)}
                onClick={() => baixarApolice.mutate(apoliceDaCotacao.id)}
              >
                Baixar apolice
              </Button>
            ) : emitida && apolice.isPending ? (
              <Button size="sm" variant="secondary" loading disabled>
                Baixar apolice
              </Button>
            ) : emitida ? (
              // Sem a apolice na lista (ainda nao lida ou fora do alcance): a lista de
              // apolices da corretora, como antes.
              <Link to="/apolices" className={linkSecundario}>
                Baixar apolice
              </Link>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                disabled
                title="A apolice existe depois da emissao pela seguradora."
              >
                Baixar apolice
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {op.stage === 'declinada' ? (
        <Alert
          tone="danger"
          title={`Cotacao ${statusLabel('cotacao', op.proposal_status ?? 'recusada').toLowerCase()}`}
        >
          {op.decline_reason ||
            'A seguradora encerrou a cotacao. O motivo fica com a seguradora.'}
        </Alert>
      ) : null}

      {op.stage === 'em_cotacao' ? (
        <Alert tone="info" title="Cotacao ainda nao enviada">
          A cotacao fica com a corretora ate o envio para analise. As verificacoes
          automaticas (cadastro, PEP, credito, subscricao e resseguro) ainda sao regra
          pendente: a cotacao segue para a analise manual da seguradora.
        </Alert>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="flex flex-col gap-5 lg:col-span-2">
          <Card>
            <CardHeader
              title="Historico da movimentacao"
              description="Do mais recente para o mais antigo, com quem executou."
              action={
                <span className="flex flex-wrap gap-1">
                  <StatusBadge kind="statusSituacao" value="aprovado" />
                  <StatusBadge kind="statusSituacao" value="recusado" />
                  <StatusBadge kind="ladoSituacao" value="seguradora" />
                  <StatusBadge kind="ladoSituacao" value="corretor" />
                </span>
              }
            />
            <ul className="divide-y divide-line">
              {gruposDaMovimentacao.map((grupo) => {
                const ultimo = tempo.items.find(
                  (item: TimelineEntry) => item.situation_group === grupo,
                );
                return (
                  <li
                    key={grupo}
                    className="flex flex-wrap items-center justify-between gap-2 px-5 py-3"
                  >
                    <span className="text-sm font-medium text-ink-800">
                      {statusLabel('grupoSituacao', grupo)}
                    </span>
                    {ultimo ? (
                      <span className="flex flex-wrap items-center gap-1.5 text-2xs text-ink-600">
                        {ultimo.situation ?? ultimo.summary}
                        {ultimo.situation_status ? (
                          <StatusBadge
                            kind="statusSituacao"
                            value={ultimo.situation_status}
                          />
                        ) : null}
                        {ultimo.actor_side ? (
                          <StatusBadge kind="ladoSituacao" value={ultimo.actor_side} />
                        ) : null}
                        {ultimo.actor_label ? `${ultimo.actor_label} - ` : ''}
                        {formatDateTime(ultimo.created_at)}
                      </span>
                    ) : (
                      <span className="text-2xs text-ink-400">Sem movimentacao</span>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="border-t border-line">
              <Timeline items={tempo.items.map(itemDaLinhaDoTempo)} />
            </div>
          </Card>
        </div>

        <div className="flex flex-col gap-5">
          <Card>
            <CardHeader title="Dados da cotacao" />
            <CardBody>
              <dl className="grid gap-3 text-sm">
                <Dado rotulo="Nro. da Cotacao">{numero}</Dado>
                <Dado rotulo="Tomador">
                  {op.policyholder.label}
                  {op.policyholder.document
                    ? ` - ${formatDocument(op.policyholder.document)}`
                    : ''}
                </Dado>
                {op.policyholder_branch ? (
                  <Dado rotulo="Filial">
                    {op.policyholder_branch.label}
                    {op.policyholder_branch.sublabel
                      ? ` - ${formatDocument(op.policyholder_branch.sublabel)}`
                      : ''}
                  </Dado>
                ) : null}
                <Dado rotulo="Segurado">
                  {op.insured
                    ? `${op.insured.label} - ${formatDocument(op.insured.document)}`
                    : '-'}
                </Dado>
                <Dado rotulo="Grupo/Modalidade">
                  {grupoDaModalidade ? `${grupoDaModalidade} / ` : ''}
                  {op.modality.label}
                </Dado>
                <Dado rotulo="Canal">{op.channel?.label ?? 'Canal nao definido'}</Dado>
                <Dado rotulo="Importancia Segurada">
                  {formatMoney(op.insured_amount)}
                </Dado>
                <Dado rotulo="Premio">{formatMoney(op.premium)}</Dado>
                <Dado rotulo="Vigencia">
                  {op.coverage_start || op.coverage_end
                    ? `${formatCivilDate(op.coverage_start)} a ${formatCivilDate(op.coverage_end)}`
                    : '-'}
                </Dado>
                <Dado rotulo="Data da cotacao">{formatDateTime(op.created_at)}</Dado>
              </dl>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Documentos solicitados"
              description="Pedidos pela seguradora nesta cotacao."
              action={
                info.data?.has_pending_documents ? (
                  <Badge tone="warning">Ha documento pendente</Badge>
                ) : null
              }
            />
            {info.isError ? (
              <CardBody>
                <Alert tone="danger" title="Nao foi possivel carregar">
                  {info.error.message}
                </Alert>
              </CardBody>
            ) : (
              <BrokerRequestedDocuments
                operationId={op.id}
                documentos={info.data?.requested_documents ?? []}
              />
            )}
          </Card>

          <CommentsPanel
            operationId={op.id}
            description="Mensagens com a seguradora sobre esta cotacao. A conversa interna da seguradora nao aparece aqui."
          />
        </div>
      </div>

      <QuoteWithdrawDialog
        operationId={op.id}
        open={cancelando}
        onClose={() => setCancelando(false)}
      />
      <IssuanceRequestDialog
        operationId={op.id}
        open={pedindoEmissao}
        onClose={() => setPedindoEmissao(false)}
      />
      <BoletosDaCotacao
        operationId={op.id}
        numero={numero}
        emitida={emitida}
        apolice={apoliceDaCotacao}
        open={vendoBoletos}
        onClose={() => setVendoBoletos(false)}
      />
    </div>
  );
}

function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-2xs text-ink-500">{rotulo}</dt>
      <dd className="text-ink-800">{children}</dd>
    </div>
  );
}
